import { Injectable } from '@angular/core';
import { catchAndContinue } from 'src/app/lego-utils';
import { of, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Colour, FamilyTree } from 'src/app/models/domain';
import { ColourDataService, FamilyTreeDataService, MultiColourDataService } from 'src/app/services';
import { handleError } from 'src/app/state/akita-error-helper';
import { CollectionState } from 'src/app/state/collection-state';
import { ItemUiState } from 'src/app/state/item-ui-state';
import { upsertById } from 'src/app/state/upsert-by-id';

export interface IFamilyTreeUI {
  loading: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FamilyTreeService {
  private readonly state = new CollectionState<FamilyTree>();
  private readonly uiState = new ItemUiState<IFamilyTreeUI>();

  public readonly familyTrees$ = this.state.items$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;
  public readonly uiById$ = this.uiState.byId$;

  public constructor(
    private readonly familyTreeDataService: FamilyTreeDataService,
    private readonly colourDataService: ColourDataService,
    private readonly multiColourDataService: MultiColourDataService
  ) {}

  public loadByDesignId(designId: string) {
    this.state.setLoading(true);

    return catchAndContinue(
      this.familyTreeDataService.getFamilyTreesByDesignId(designId),
      (error) => {
        handleError(this.state, error);
      }
    ).pipe(
      tap((familyTrees) => {
        this.state.set(familyTrees);
        // Akita's setInitialEntityState created a UI entity per item as the collection
        // was written, so `loading: true` was implicit. Here it is explicit.
        this.uiState.setMany(familyTrees.map(({ id }) => [id, { loading: true }]));
      }),
      switchMap((familyTrees) => {
        const colourIds = familyTrees.map((tree) => tree.colourId);

        const colour$ = catchAndContinue(
          this.colourDataService.getColoursByColourIds(colourIds),
          (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            this.uiState.patchAll({ loading: false, error: errorMessage });
          }
        );

        return zip(of(familyTrees), colour$);
      }),
      map(([familyTrees, colours]) => {
        const familyTreesWithColours = familyTrees.map((tree): FamilyTree => ({
          ...tree,
          colours: colours.filter((colour) => colour.colourId === tree.colourId),
        }));

        const familyTreeWithoutMultiColours = familyTreesWithColours.filter(
          (tree) => tree.colours && !tree.colours.find((colour) => colour.colourType === 'M')
        );

        this.updateColours(familyTreeWithoutMultiColours);

        return familyTreesWithColours;
      }),
      switchMap((familyTrees) => {
        const materialIds = familyTrees
          .filter((tree) => tree.colours?.some((colour) => colour.colourType === 'M'))
          .map((tree) => tree.physicalMaterial);

        const multiColours$ = catchAndContinue(
          this.multiColourDataService.getMultiColoursByMaterialIds(materialIds),
          (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            this.uiState.patch(materialIds, { error: errorMessage, loading: false });
          }
        );

        const familyTreeWithMultiColours = familyTrees.filter((tree) =>
          tree.colours?.some((colour) => colour.colourType === 'M')
        );

        return zip(of(familyTreeWithMultiColours), multiColours$);
      }),
      tap(([familyTrees, multiColourWrappers]) => {
        const familyTreesWithMultiColours = familyTrees.map((tree): FamilyTree => {
          return {
            ...tree,
            colours: multiColourWrappers
              .filter((wrapper) => wrapper.materialId === tree.physicalMaterial && wrapper.colour)
              .map((wrapper) => wrapper.colour as Colour),
          };
        });

        this.updateColours(familyTreesWithMultiColours);
      }),
      map(() => undefined)
    );
  }

  /**
   * One emission for the collection and one for the UI map, which is what Akita's
   * `@transaction()` bought here — the previous version looped `replace` + `ui.replace`
   * per tree, and MaterialColourTableComponent renders off both.
   */
  private updateColours(trees: FamilyTree[]) {
    this.state.set(upsertById(this.state.items, trees));

    this.uiState.setMany(
      trees.map(({ id, colours }) => [
        id,
        {
          error: !colours || colours.length === 0 ? 'Could not find colours' : undefined,
          loading: false,
        },
      ])
    );
  }
}
